import {
  LEAVE_TYPES,
  LEAVE_TYPES_ALIASES,
  LEGACY_LEAVE_TYPE_LABELS,
  LEAVE_FOR_PERSONAL_CIRCUMSTANCES_SUB_TYPES,
  MATERNITY_LEAVE_SUB_TYPES,
  PAID_LEAVE_SUB_TYPES,
  SICK_DAY_REASONS_TO_NEW_SUBTYPE,
  SICK_DAY_SUB_TYPES,
  PAID_LEAVE_SUB_TYPES_ALIAS,
} from 'domain/shared/leave';

export type LeaveTypeDisplayInput = {
  type?: string | null;
  subType?: string | null;
  sickDayReason?: keyof typeof SICK_DAY_REASONS_TO_NEW_SUBTYPE | null;
  /** When `type` is missing (e.g. some balance rows), infer from a raw enum string. */
  leaveTypeFallback?: string | null;
  /** When `true` - Paid Leave / Regular Paid Leave is shortened to Paid Leave. */
  shortenPaidLeaveLabel?: boolean;
};

function resolveSubTypeLabel(leaveType: string, subType: string): string | undefined {
  if (leaveType === LEAVE_TYPES_ALIASES.PAID_LEAVE) {
    return PAID_LEAVE_SUB_TYPES[subType as keyof typeof PAID_LEAVE_SUB_TYPES];
  }
  if (leaveType === LEAVE_TYPES_ALIASES.SICK_DAY) {
    return SICK_DAY_SUB_TYPES[subType as keyof typeof SICK_DAY_SUB_TYPES];
  }
  if (leaveType === LEAVE_TYPES_ALIASES.LEAVE_FOR_PERSONAL_CIRCUMSTANCES) {
    return LEAVE_FOR_PERSONAL_CIRCUMSTANCES_SUB_TYPES[
      subType as keyof typeof LEAVE_FOR_PERSONAL_CIRCUMSTANCES_SUB_TYPES
    ];
  }
  if (leaveType === LEAVE_TYPES_ALIASES.MATERNITY_LEAVE) {
    return MATERNITY_LEAVE_SUB_TYPES[subType as keyof typeof MATERNITY_LEAVE_SUB_TYPES];
  }
  return undefined;
}

function resolveTypeAndSubTypeFromFallback(leaveTypeFallback: string): {
  type: string;
  subType: string | null;
} | null {
  if (leaveTypeFallback in LEAVE_TYPES) {
    return { type: leaveTypeFallback, subType: null };
  }
  if (leaveTypeFallback in LEAVE_FOR_PERSONAL_CIRCUMSTANCES_SUB_TYPES) {
    return {
      type: LEAVE_TYPES_ALIASES.LEAVE_FOR_PERSONAL_CIRCUMSTANCES,
      subType: leaveTypeFallback,
    };
  }
  if (leaveTypeFallback in SICK_DAY_SUB_TYPES) {
    return { type: LEAVE_TYPES_ALIASES.SICK_DAY, subType: leaveTypeFallback };
  }
  if (leaveTypeFallback in PAID_LEAVE_SUB_TYPES) {
    return { type: LEAVE_TYPES_ALIASES.PAID_LEAVE, subType: leaveTypeFallback };
  }
  if (leaveTypeFallback in MATERNITY_LEAVE_SUB_TYPES) {
    return { type: LEAVE_TYPES_ALIASES.MATERNITY_LEAVE, subType: leaveTypeFallback };
  }
  if (leaveTypeFallback === 'MATERNITY_LEAVE') {
    return { type: LEAVE_TYPES_ALIASES.MATERNITY_LEAVE, subType: null };
  }
  return null;
}

/**
 * Single-line label for leave type, optionally with subtype (e.g. "Paid Leave / Day On Demand").
 * Normalizes legacy top-level `COMPENSATION` to "Paid Leave / Compensation".
 */
export function formatLeaveTypeWithSubType({
  type,
  subType,
  sickDayReason,
  leaveTypeFallback,
  shortenPaidLeaveLabel = true,
}: LeaveTypeDisplayInput): string {
  let resolvedType = type;
  let resolvedSubType = subType;

  if (!resolvedType && leaveTypeFallback) {
    const inferred = resolveTypeAndSubTypeFromFallback(leaveTypeFallback);
    if (inferred) {
      resolvedType = inferred.type;
      resolvedSubType = inferred.subType;
    } else {
      return leaveTypeFallback;
    }
  }

  if (!resolvedType) {
    return '';
  }

  if (resolvedType === 'COMPENSATION') {
    return `${LEAVE_TYPES.PAID_LEAVE} / ${PAID_LEAVE_SUB_TYPES.COMPENSATION}`;
  }

  const effectiveSubType =
    resolvedSubType || (sickDayReason ? SICK_DAY_REASONS_TO_NEW_SUBTYPE[sickDayReason] : null) || null;

  if (shortenPaidLeaveLabel && effectiveSubType === PAID_LEAVE_SUB_TYPES_ALIAS.REGULAR_PAID_LEAVE) {
    return LEAVE_TYPES.PAID_LEAVE;
  }

  const baseLabel = LEAVE_TYPES[resolvedType as keyof typeof LEAVE_TYPES];
  if (!baseLabel) {
    return LEGACY_LEAVE_TYPE_LABELS[resolvedType as keyof typeof LEGACY_LEAVE_TYPE_LABELS] ?? resolvedType;
  }

  if (!effectiveSubType) {
    return baseLabel;
  }

  const subLabel = resolveSubTypeLabel(resolvedType, effectiveSubType);
  if (!subLabel) {
    return baseLabel;
  }

  return `${baseLabel} / ${subLabel}`;
}
